import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AdminSection } from "./admin";

export const Route = createFileRoute("/_authenticated/admin/analytics")({
  head: () => ({
    meta: [
      { title: "Analytics · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminAnalytics,
});

type Row = { event: string; path: string | null; created_at: string; session_id: string | null };
type Window = "24h" | "7d" | "30d";

const RANGES: Record<Window, number> = { "24h": 1, "7d": 7, "30d": 30 };

function since(days: number) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString();
}

function AdminAnalytics() {
  const [win, setWin] = useState<Window>("7d");
  const [rows, setRows] = useState<Row[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setRows(null);
    setErr(null);
    (async () => {
      const from = since(RANGES[win]);
      const [{ data, error }, { count }] = await Promise.all([
        supabase
          .from("analytics_events")
          .select("event,path,created_at,session_id")
          .gte("created_at", from)
          .order("created_at", { ascending: false })
          .limit(5000),
        supabase
          .from("analytics_events")
          .select("*", { count: "exact", head: true })
          .gte("created_at", from),
      ]);
      if (!alive) return;
      if (error) {
        setErr(error.message);
        setRows([]);
        return;
      }
      setRows((data ?? []) as Row[]);
      setTotal(count ?? 0);
    })();
    return () => {
      alive = false;
    };
  }, [win]);

  const byEvent = new Map<string, number>();
  const byPath = new Map<string, number>();
  const sessions = new Set<string>();
  (rows ?? []).forEach((r) => {
    byEvent.set(r.event, (byEvent.get(r.event) ?? 0) + 1);
    if (r.event === "pageview" && r.path) {
      byPath.set(r.path, (byPath.get(r.path) ?? 0) + 1);
    }
    if (r.session_id) sessions.add(r.session_id);
  });

  const events = [...byEvent.entries()].sort((a, b) => b[1] - a[1]);
  const paths = [...byPath.entries()].sort((a, b) => b[1] - a[1]).slice(0, 15);

  return (
    <AdminSection
      title="Analytics"
      description="First-party site events. Recorded only after visitors accept cookies. No personal identifiers stored."
      action={
        <div className="flex gap-1 rounded-full border hairline p-1">
          {(Object.keys(RANGES) as Window[]).map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => setWin(w)}
              className={`rounded-full px-3 py-1 text-xs uppercase tracking-[0.2em] transition ${
                win === w ? "bg-gold-gradient text-obsidian font-semibold" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {w}
            </button>
          ))}
        </div>
      }
    >
      {err && (
        <p className="mb-6 rounded-lg border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
          Couldn't load analytics: {err}
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-3">
        <Metric label="Events" value={total} loading={rows === null} />
        <Metric label="Unique sessions" value={rows === null ? null : sessions.size} loading={rows === null} />
        <Metric
          label="Pageviews"
          value={rows === null ? null : byEvent.get("pageview") ?? 0}
          loading={rows === null}
        />
      </div>

      <div className="mt-10 grid gap-8 lg:grid-cols-2">
        <Card title="Top events">
          {rows === null ? (
            <SkeletonList />
          ) : events.length === 0 ? (
            <Empty />
          ) : (
            <ul className="divide-y hairline">
              {events.map(([name, n]) => (
                <li key={name} className="flex items-center justify-between py-2.5">
                  <span className="text-sm">{name}</span>
                  <span className="font-mono text-sm text-gold-luminous">{n.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title="Top pages">
          {rows === null ? (
            <SkeletonList />
          ) : paths.length === 0 ? (
            <Empty />
          ) : (
            <ul className="divide-y hairline">
              {paths.map(([path, n]) => (
                <li key={path} className="flex items-center justify-between gap-3 py-2.5">
                  <span className="truncate text-sm text-muted-foreground">{path}</span>
                  <span className="shrink-0 font-mono text-sm text-gold-luminous">{n.toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminSection>
  );
}

function Metric({ label, value, loading }: { label: string; value: number | null; loading: boolean }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{label}</p>
      <p className="mt-3 font-serif text-4xl text-gold-luminous">
        {loading ? "—" : value?.toLocaleString() ?? "0"}
      </p>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border hairline p-6">
      <h3 className="font-serif text-xl">{title}</h3>
      <div className="mt-4">{children}</div>
    </div>
  );
}

function SkeletonList() {
  return (
    <ul className="space-y-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <li key={i} className="h-6 animate-pulse rounded bg-muted/10" />
      ))}
    </ul>
  );
}

function Empty() {
  return <p className="text-sm text-muted-foreground">No events yet in this window.</p>;
}
